import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { getMessages } from '../constants'
import { OrderedListNumber, StepBox, StepLabel } from '../style'
import { Actions, ErorrWrapper, InstructorCard, InstructorCards } from './style'
import Checkbox from '@berlitz/checkbox'
import Select from '@components/Select'
import { getSortOptions } from './constants'
import Spacer from '@berlitz/spacer'
import PortalAvatar from '@classes/Avatar'
import { ISelectInstructors, ISort } from './interface'
import { useBlockedInstructors, useFave } from '../../context'
import { orderBy, sortBy } from 'lodash'
import { useFMIBulkForm } from '../Form/useFMIBulkForm'
import HR from '@components/HR/HR'
import { useFMIBulkSelectInstructors } from './useSelectInstructors'
import EmptyResults from '../../EmptyResults'
import InstructorSearchResultCardBulk from '@berlitzplatforms/micro.ui.instructor-search-result-card-bulk'
import ProfileModal from './ProfileModal'
import PortalInstructorProfile from '@classes/InstructorProfile'
import EmptyState from '@components/EmptyState'
import { H3 } from '@berlitz/globals'
import { StepWarning } from '../Form/Fields'
import IconWrapper from '@components/IconWrapper'
import BlockModal from '../../BlockModal'

const SelectInstructors: React.FC<ISelectInstructors> = () => {
  const intl = useIntl()
  const { isFormPristine, isFetching, hasFetchError, fetchedInstructors, Instructors, setInstructors, showWarning } =
    useFMIBulkForm()
  const fave = useFave()
  const blocked = useBlockedInstructors()
  const { selectAll, setSelectAll, sort, setSort, selectedInstructorIds, setSelectedInstructorIds } =
    useFMIBulkSelectInstructors()
  const messages = getMessages()
  const [openProfileModal, setOpenProfileModal] = useState(false)
  const [openBlockModal, setOpenBlockModal] = useState(false)
  const [profile, setProfile] = useState<PortalInstructorProfile | null>(null)

  if (isFormPristine) {
    return null
  }

  const onSortChange = (sort: ISort) => {
    setInstructors(
      orderBy(fetchedInstructors, 'Ranking', sort).map(
        (fetchedInstructor) => Instructors.find(({ InstructorId }) => InstructorId === fetchedInstructor.InstructorId)!
      )
    )
    setSort(sort)
  }

  if (!isFormPristine && !hasFetchError && !isFetching && !fetchedInstructors.length) {
    return (
      <>
        <Spacer size="md" />
        <EmptyResults />
      </>
    )
  }

  if (hasFetchError && !isFetching) {
    return (
      <ErorrWrapper>
        <Spacer size="md" />
        <H3 disableMargin>{intl.formatMessage(messages.errorHeader)}.</H3>
        <EmptyState heading="Please try again later or contact the Support team." img="/images/bug.svg" />
      </ErorrWrapper>
    )
  }

  if (hasFetchError && !isFetching) {
    return (
      <ErorrWrapper>
        <Spacer size="md" />
        <H3 disableMargin>{intl.formatMessage(messages.errorHeader)}.</H3>
        <EmptyState heading="Please try again later or contact the Support team." img="/images/bug.svg" />
      </ErorrWrapper>
    )
  }

  return (
    <>
      <StepBox className="step-with-warning">
        <StepBox>
          <OrderedListNumber>2</OrderedListNumber>
          <StepLabel>{intl.formatMessage(messages.step2Label)}</StepLabel>
        </StepBox>

        {showWarning() ? <StepWarning /> : null}
      </StepBox>

      <Spacer size="xs" />

      <Actions>
        <div>
          <Checkbox
            checked={selectedInstructorIds.length > 0 && selectedInstructorIds.length === Instructors.length}
            label={intl.formatMessage(messages.selectAll)}
            name="selectAll"
            value={`${selectAll}`}
            onChange={(val) => {
              if (!isFetching) {
                const isUnchecked = val.target.value === 'false'
                setSelectedInstructorIds(isUnchecked ? Instructors.map(({ InstructorId }) => InstructorId!) : [])
                setSelectAll(isUnchecked)
              }
            }}
            disabled={isFetching}
          />
        </div>

        <div className="select">
          <div>{intl.formatMessage(messages.sort)}</div>
          <label
            style={{
              flexShrink: 1,
            }}
          >
            <Select
              onChange={onSortChange}
              options={getSortOptions().map(({ label, ...rest }) => ({
                ...rest,
                label: intl.formatMessage({
                  id: label,
                  defaultMessage: label,
                }),
              }))}
              disableMargin
              responsive
              placeholderText={intl.formatMessage(messages.mostAvailable)}
              fullWidth
              value={sort}
              disabled={isFetching}
            />
          </label>
        </div>
      </Actions>

      <InstructorCards>
        {isFetching
          ? [...Array(9)].map((_, index) => (
              <InstructorCard key={`instructor-loading-${index}`}>
                <InstructorSearchResultCardBulk Id="" Name="" Avatar="" isLoading />
                <Spacer size="xs" />
              </InstructorCard>
            ))
          : Instructors?.map((Instructor, index) => {
              const InstructorId = Instructor.InstructorId
              const InstructorAvatar = new PortalAvatar(Instructor.Name)?.Avatar
              const isFaved = fave.isFaved(InstructorId)
              const isSelected = selectedInstructorIds.some((Id) => Id === InstructorId)
              const onCheckboxClick = () =>
                setSelectedInstructorIds(
                  isSelected
                    ? selectedInstructorIds.filter((Id) => Id !== InstructorId)
                    : [...selectedInstructorIds, InstructorId!]
                )

              return (
                <InstructorCard key={`instructor-${index}`}>
                  <IconWrapper>
                    <InstructorSearchResultCardBulk
                      Id={InstructorId}
                      Name={Instructor.Name!}
                      Avatar={InstructorAvatar}
                      isSelected={isSelected}
                      isFaved={isFaved}
                      onSelect={onCheckboxClick}
                      onNameClick={() => {
                        setProfile(new PortalInstructorProfile(Instructor))
                        setOpenProfileModal(true)
                      }}
                      onFaveClick={() => {
                        if (fave.enableActions) {
                          const newFaves = fave.faveInstructors.filter(({ InstructorId: Id }) => Id !== InstructorId)

                          if (isFaved) {
                            fave.setFaveInstructors(newFaves)
                            fave.deleteRelatedInstructors({
                              variables: fave.getPayload('delete', [Instructor]),
                            })
                          } else {
                            fave.setFaveInstructors(sortBy([...newFaves, Instructor], ['Name']))
                            fave.putRelatedInstructors({
                              variables: fave.getPayload('put', [Instructor]),
                            })
                          }
                        }
                      }}
                      isBlocked={Instructor.IsBlocked}
                      withBlockedBtn={blocked.allowed}
                      onBlockedClick={() => {
                        if (blocked.enableActions) {
                          blocked.setSelectedInstructor(Instructor)
                          setOpenBlockModal(true)
                        }
                      }}
                      disableFaveActions={!fave.enableActions}
                      disableBlockActions={!blocked.enableActions}
                    />
                  </IconWrapper>
                  <Spacer size="xs" />
                </InstructorCard>
              )
            })}
      </InstructorCards>

      <Spacer size="sm" />

      <Spacer size="xxs" />

      <HR />

      <ProfileModal
        open={openProfileModal}
        onClose={() => setOpenProfileModal(false)}
        Profile={profile}
        onCompleted={(id) => setInstructors(Instructors?.filter(({ InstructorId }) => InstructorId !== id) || [])}
      />

      <BlockModal
        open={openBlockModal && !!blocked.selectedInstructor}
        onClose={() => {
          blocked.setSelectedInstructor(undefined)
          setOpenBlockModal(false)
        }}
        onCompleted={(id) => setInstructors(Instructors?.filter(({ InstructorId }) => InstructorId !== id) || [])}
      />
    </>
  )
}

export default SelectInstructors
